import {
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  Inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormGroupDirective,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  DateAdapter,
  MatDateFormats,
  MAT_DATE_FORMATS,
} from '@angular/material/core';
import { MatCalendarView } from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LocaleFacade } from '@rd/core/facades/locale-facade/locale.facade';
import { Locale } from '@rd/types';
import dayjs from 'dayjs';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'rd-shared-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line no-use-before-define
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
})
export class DatePickerComponent implements ControlValueAccessor, OnInit {
  @HostBinding('class.rd-shared-date-picker') componentClass = true;

  @ViewChild(MatInput, { read: ElementRef, static: true }) input: ElementRef;
  @Input() dateFormat: string;
  @Input() customDateValidationFn: () => ValidatorFn;

  // Currently min and max dates are fixed for this component.
  // In the future, if you need to customize this value, just pass it as a param
  // from the client.
  maxDate = dayjs().subtract(1, 'day');
  minDate = dayjs('1001');

  currentCalendarView: MatCalendarView;
  dateControl: UntypedFormControl;

  localeSelected$: Observable<Locale | null> =
    this.localeFacade.selectedLocale$;

  constructor(
    private localeFacade: LocaleFacade,
    private dateAdapter: DateAdapter<any>,
    public formGroup: FormGroupDirective,
    @Inject(MAT_DATE_FORMATS) public dateFormatConfig: MatDateFormats,
  ) {}

  ngOnInit(): void {
    this.dateFormatConfig.display.dateInput = this.dateFormat || 'YYYY/MM/DD';
    this.customDateValidationFn = this.customDateValidationFn || null;

    this.dateControl = new UntypedFormControl('', [
      Validators.required,
      this.customDateValidationFn(),
    ]);
    this.localeSelected$
      .pipe(
        untilDestroyed(this),
        tap((locale) => {
          this.updateLocaleSelected(locale as Locale);
        }),
      )
      .subscribe();
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.dateControl.disable();
    } else {
      this.dateControl.enable();
    }
  }

  writeValue(value: Date): void {
    this.dateControl.setValue(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
    this.dateControl.valueChanges
      .pipe(untilDestroyed(this), tap(fn))
      .subscribe();
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onChange: any = () => {};

  onTouched = (): void => {};

  onFocusIn(): void {
    this.onTouched();
  }

  updateLocaleSelected(locale: Locale): void {
    this.dateAdapter.setLocale(locale.code);
  }
}
